// src/components/Sidebar.js

import React, { useCallback, useEffect, useState } from "react";

import {
  LayoutGrid,
  Package,
  Receipt,
  Tag,
  Users,
  BarChart3,
  Building2,
  PieChart,
  Wallet,
  Table2,
  CreditCard,
  History,
  X as CloseIcon,
} from "lucide-react";

import { getKitchenCount } from "../utils/kitchenBadge";

export default function Sidebar({
  currentView,
  onViewChange,
  role,
  mobileOpen,
  setMobileOpen,
}) {
  const [kitchenCount, setKitchenCount] = useState(() =>
    getKitchenCount()
  );

  // =====================================================
  // UPDATE KITCHEN ORDER BADGE
  // =====================================================

  useEffect(() => {
    const updateKitchenCount = () => {
      setKitchenCount(getKitchenCount());
    };

    updateKitchenCount();

    const intervalId = setInterval(updateKitchenCount, 1000);

    // Also update when the user returns to the browser tab.
    window.addEventListener("focus", updateKitchenCount);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", updateKitchenCount);
    };
  }, []);

  // =====================================================
  // NAVIGATION
  // =====================================================

  const handleViewChange = useCallback(
    (view) => {
      if (typeof onViewChange === "function") {
        onViewChange(view);
      }

      // Close the mobile drawer after selecting a page.
      if (typeof setMobileOpen === "function") {
        setMobileOpen(false);
      }
    },
    [onViewChange, setMobileOpen]
  );

  // =====================================================
  // BUTTON STYLES
  // =====================================================

  const btnClass = (view) =>
    `so-side-btn w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2
     text-left
     ${currentView === view ? "so-side-btn-active" : ""}`;

  // =====================================================
  // REUSABLE KITCHEN ORDER BUTTON
  // =====================================================

  const KitchenOrdersButton = () => (
    <button
      type="button"
      onClick={() => handleViewChange("chefOrders")}
      className={`${btnClass("chefOrders")} relative`}
    >
      <Receipt size={18} />

      <span>Kitchen Orders</span>

      {kitchenCount > 0 && (
        <span className="so-side-badge absolute right-3 top-1/2 -translate-y-1/2 min-w-[20px] h-5 px-1 text-xs font-semibold rounded-full flex items-center justify-center">
          {kitchenCount > 99 ? "99+" : kitchenCount}
        </span>
      )}
    </button>
  );

  // =====================================================
  // POS MENU
  // AVAILABLE TO ADMIN AND USER
  // =====================================================

  const PosMenu = () => (
    <>
      <button
        type="button"
        onClick={() => handleViewChange("pos")}
        className={btnClass("pos")}
      >
        <LayoutGrid size={18} />
        <span>POS</span>
      </button>

      <button
        type="button"
        onClick={() => handleViewChange("directPos")}
        className={btnClass("directPos")}
      >
        <LayoutGrid size={18} />
        <span>Direct POS</span>
      </button>

      <button
        type="button"
        onClick={() => handleViewChange("tables")}
        className={btnClass("tables")}
      >
        <Table2 size={18} />
        <span>Restaurant Tables</span>
      </button>

      {/* ORDER HISTORY */}
      <button
        type="button"
        onClick={() => handleViewChange("orderHistory")}
        className={btnClass("orderHistory")}
      >
        <History size={18} />
        <span>Order History</span>
      </button>
    </>
  );

  // =====================================================
  // ADMIN MENU
  // =====================================================

  const AdminMenu = () => (
    <>
      <button
        type="button"
        onClick={() => handleViewChange("analytics")}
        className={btnClass("analytics")}
      >
        <BarChart3 size={18} />
        <span>Dashboard</span>
      </button>

      <button
        type="button"
        onClick={() => handleViewChange("menu")}
        className={btnClass("menu")}
      >
        <Package size={18} />
        <span>Menu Items</span>
      </button>

      <button
        type="button"
        onClick={() => handleViewChange("categories")}
        className={btnClass("categories")}
      >
        <Tag size={18} />
        <span>Categories</span>
      </button>

      <button
        type="button"
        onClick={() => handleViewChange("users")}
        className={btnClass("users")}
      >
        <Users size={18} />
        <span>Users</span>
      </button>

      <KitchenOrdersButton />

      <button
        type="button"
        onClick={() => handleViewChange("parties")}
        className={btnClass("parties")}
      >
        <Users size={18} />
        <span>Parties</span>
      </button>

      <button
        type="button"
        onClick={() => handleViewChange("expenses")}
        className={btnClass("expenses")}
      >
        <Wallet size={18} />
        <span>Expenses</span>
      </button>

      <button
        type="button"
        onClick={() => handleViewChange("reports")}
        className={btnClass("reports")}
      >
        <PieChart size={18} />
        <span>Reports</span>
      </button>

      <button
        type="button"
        onClick={() => handleViewChange("profile")}
        className={btnClass("profile")}
      >
        <Building2 size={18} />
        <span>Business Profile</span>
      </button>

      <button
        type="button"
        onClick={() => handleViewChange("subscription")}
        className={btnClass("subscription")}
      >
        <CreditCard size={18} />
        <span>Subscription</span>
      </button>
    </>
  );

  // =====================================================
  // CHEF MENU
  // =====================================================

  const ChefMenu = () => (
    <>
      <KitchenOrdersButton />
    </>
  );

  // =====================================================
  // MENU BASED ON ROLE
  // =====================================================

  const MenuButtons = () => {
    const normalizedRole = String(role || "")
      .trim()
      .toUpperCase();

    if (normalizedRole === "CHEF") {
      return <ChefMenu />;
    }

    if (normalizedRole === "USER") {
      return <PosMenu />;
    }

    // ADMIN: POS menu + administration menu
    return (
      <>
        <PosMenu />
        <div className="so-side-divider my-4" />
        <AdminMenu />
      </>
    );
  };

  // =====================================================
  // SIDEBAR HEADER
  // =====================================================

  const SidebarHeader = ({ mobile = false }) => (
    <div
      className={`so-side-head flex items-center justify-between ${
        mobile ? "mb-4" : "p-6"
      }`}
    >
      <div>
        <h1
          className={`so-side-brand ${
            mobile ? "so-side-brand-sm" : ""
          }`}
        >
          Swift<span className="so-side-brand-accent">Order</span>
        </h1>

        <p className="so-side-role">{role || "USER"}</p>
      </div>

      {mobile && (
        <button
          type="button"
          aria-label="Close navigation menu"
          className="so-side-close p-2 rounded-lg"
          onClick={() => setMobileOpen(false)}
        >
          <CloseIcon size={22} />
        </button>
      )}
    </div>
  );

  // =====================================================
  // MOBILE SIDEBAR
  // =====================================================

  const MobileSidebar = () => (
    <div
      className={`fixed inset-0 z-40 md:hidden ${
        mobileOpen
          ? "visible"
          : "invisible pointer-events-none"
      }`}
      aria-hidden={!mobileOpen}
    >
      {/* Overlay */}
      <button
        type="button"
        aria-label="Close navigation menu"
        tabIndex={mobileOpen ? 0 : -1}
        className={`absolute inset-0 bg-black/50 transition-opacity ${
          mobileOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={() => setMobileOpen(false)}
      />

      {/* Drawer */}
      <aside
        className={`so-side-panel absolute left-0 top-0 bottom-0
          w-64 max-w-[85vw] p-4
          flex flex-col shadow-xl
          transform transition-transform duration-200
          ${
            mobileOpen
              ? "translate-x-0"
              : "-translate-x-full"
          }`}
      >
        <SidebarHeader mobile />

        <nav className="flex-1 overflow-y-auto">
          <MenuButtons />
        </nav>
      </aside>
    </div>
  );

  // =====================================================
  // DESKTOP SIDEBAR
  // =====================================================

  return (
    <>
      {/* Scoped SwiftOrder theme for the sidebar — ticket-ink background,
          chili active state, same tokens as Login.js / Dashboard.js. */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

        .so-side-panel, .so-side-panel-desktop {
          --ink: #1c1a17;
          --paper: #fbf9f4;
          --chili: #e4572e;
          --chili-dark: #c43f1a;
          --herb: #3f6b4f;
          --stone-light: #a49c8d;
          --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
          --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          --font-mono: 'JetBrains Mono', 'Courier New', monospace;

          background: var(--ink);
          color: var(--paper);
          font-family: var(--font-body);
        }

        .so-side-panel *, .so-side-panel-desktop * {
          box-sizing: border-box;
        }

        .so-side-head {
          border-bottom: 1px solid rgba(251, 249, 244, 0.14);
        }

        .so-side-brand {
          font-family: var(--font-display);
          font-size: 1.6rem;
          letter-spacing: 0.01em;
          color: var(--paper);
          line-height: 1.1;
        }

        .so-side-brand-sm {
          font-size: 1.25rem;
        }

        .so-side-brand-accent {
          color: var(--chili);
        }

        .so-side-role {
          font-family: var(--font-mono);
          font-size: 0.7rem;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--stone-light);
          margin-top: 4px;
        }

        .so-side-close {
          color: var(--stone-light);
          background: transparent;
          transition: background 0.15s ease, color 0.15s ease;
        }

        .so-side-close:hover {
          background: rgba(251, 249, 244, 0.08);
          color: var(--paper);
        }

        .so-side-btn {
          color: var(--stone-light);
          background: transparent;
          font-size: 0.92rem;
          font-weight: 500;
          transition: background 0.15s ease, color 0.15s ease;
        }

        .so-side-btn:hover {
          background: rgba(251, 249, 244, 0.08);
          color: var(--paper);
        }

        .so-side-btn-active {
          background: var(--chili);
          color: var(--paper);
          font-weight: 700;
        }

        .so-side-btn-active:hover {
          background: var(--chili-dark);
          color: var(--paper);
        }

        .so-side-badge {
          background: var(--paper);
          color: var(--chili-dark);
        }

        .so-side-btn-active .so-side-badge {
          background: var(--ink);
          color: var(--paper);
        }

        .so-side-divider {
          border-top: 1px dashed rgba(251, 249, 244, 0.18);
        }
      `}</style>

      <MobileSidebar />

      <aside className="so-side-panel-desktop hidden md:flex md:flex-col md:w-64 md:shrink-0 h-screen">
        <SidebarHeader />

        <nav className="p-4 flex-1 overflow-y-auto">
          <MenuButtons />
        </nav>
      </aside>
    </>
  );
}




// // src/components/Sidebar.js

// import React, { useCallback, useEffect, useState } from "react";

// import {
//   LayoutGrid,
//   Package,
//   Receipt,
//   Tag,
//   Users,
//   BarChart3,
//   Building2,
//   PieChart,
//   Wallet,
//   Table2,
//   CreditCard,
//   History,
//   X as CloseIcon,
// } from "lucide-react";

// import { getKitchenCount } from "../utils/kitchenBadge";

// export default function Sidebar({
//   currentView,
//   onViewChange,
//   role,
//   mobileOpen,
//   setMobileOpen,
// }) {
//   const [kitchenCount, setKitchenCount] = useState(() =>
//     getKitchenCount()
//   );

//   // =====================================================
//   // UPDATE KITCHEN ORDER BADGE
//   // =====================================================

//   useEffect(() => {
//     const updateKitchenCount = () => {
//       setKitchenCount(getKitchenCount());
//     };

//     updateKitchenCount();

//     const intervalId = setInterval(updateKitchenCount, 1000);

//     // Also update when the user returns to the browser tab.
//     window.addEventListener("focus", updateKitchenCount);

//     return () => {
//       clearInterval(intervalId);
//       window.removeEventListener("focus", updateKitchenCount);
//     };
//   }, []);

//   // =====================================================
//   // NAVIGATION
//   // =====================================================

//   const handleViewChange = useCallback(
//     (view) => {
//       if (typeof onViewChange === "function") {
//         onViewChange(view);
//       }

//       // Close the mobile drawer after selecting a page.
//       if (typeof setMobileOpen === "function") {
//         setMobileOpen(false);
//       }
//     },
//     [onViewChange, setMobileOpen]
//   );

//   // =====================================================
//   // BUTTON STYLES
//   // =====================================================

//   const btnClass = (view) =>
//     `w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2
//      text-left transition-colors duration-200
//      ${
//        currentView === view
//          ? "bg-blue-600 text-white"
//          : "text-slate-300 hover:bg-slate-800 hover:text-white"
//      }`;

//   // =====================================================
//   // REUSABLE KITCHEN ORDER BUTTON
//   // =====================================================

//   const KitchenOrdersButton = () => (
//     <button
//       type="button"
//       onClick={() => handleViewChange("chefOrders")}
//       className={`${btnClass("chefOrders")} relative`}
//     >
//       <Receipt size={18} />

//       <span>Kitchen Orders</span>

//       {kitchenCount > 0 && (
//         <span
//           className="
//             absolute right-3 top-1/2 -translate-y-1/2
//             min-w-[20px] h-5 px-1
//             bg-red-600 text-white text-xs font-semibold
//             rounded-full flex items-center justify-center
//           "
//         >
//           {kitchenCount > 99 ? "99+" : kitchenCount}
//         </span>
//       )}
//     </button>
//   );

//   // =====================================================
//   // POS MENU
//   // AVAILABLE TO ADMIN AND USER
//   // =====================================================

//   const PosMenu = () => (
//     <>
//       <button
//         type="button"
//         onClick={() => handleViewChange("pos")}
//         className={btnClass("pos")}
//       >
//         <LayoutGrid size={18} />
//         <span>POS</span>
//       </button>

//       <button
//         type="button"
//         onClick={() => handleViewChange("directPos")}
//         className={btnClass("directPos")}
//       >
//         <LayoutGrid size={18} />
//         <span>Direct POS</span>
//       </button>

//       <button
//         type="button"
//         onClick={() => handleViewChange("tables")}
//         className={btnClass("tables")}
//       >
//         <Table2 size={18} />
//         <span>Restaurant Tables</span>
//       </button>

//       <button
//         type="button"
//         onClick={() => handleViewChange("orders")}
//         className={btnClass("orders")}
//       >
//         <Receipt size={18} />
//         <span>Orders</span>
//       </button>

//       {/* NEW: ORDER HISTORY */}
//       <button
//         type="button"
//         onClick={() => handleViewChange("orderHistory")}
//         className={btnClass("orderHistory")}
//       >
//         <History size={18} />
//         <span>Order History</span>
//       </button>
//     </>
//   );

//   // =====================================================
//   // ADMIN MENU
//   // =====================================================

//   const AdminMenu = () => (
//     <>
//       <button
//         type="button"
//         onClick={() => handleViewChange("analytics")}
//         className={btnClass("analytics")}
//       >
//         <BarChart3 size={18} />
//         <span>Dashboard</span>
//       </button>

//       <button
//         type="button"
//         onClick={() => handleViewChange("menu")}
//         className={btnClass("menu")}
//       >
//         <Package size={18} />
//         <span>Menu Items</span>
//       </button>

//       <button
//         type="button"
//         onClick={() => handleViewChange("categories")}
//         className={btnClass("categories")}
//       >
//         <Tag size={18} />
//         <span>Categories</span>
//       </button>

//       <button
//         type="button"
//         onClick={() => handleViewChange("users")}
//         className={btnClass("users")}
//       >
//         <Users size={18} />
//         <span>Users</span>
//       </button>

//       <KitchenOrdersButton />

//       <button
//         type="button"
//         onClick={() => handleViewChange("parties")}
//         className={btnClass("parties")}
//       >
//         <Users size={18} />
//         <span>Parties</span>
//       </button>

//       <button
//         type="button"
//         onClick={() => handleViewChange("expenses")}
//         className={btnClass("expenses")}
//       >
//         <Wallet size={18} />
//         <span>Expenses</span>
//       </button>

//       <button
//         type="button"
//         onClick={() => handleViewChange("reports")}
//         className={btnClass("reports")}
//       >
//         <PieChart size={18} />
//         <span>Reports</span>
//       </button>

//       <button
//         type="button"
//         onClick={() => handleViewChange("profile")}
//         className={btnClass("profile")}
//       >
//         <Building2 size={18} />
//         <span>Business Profile</span>
//       </button>

//       <button
//         type="button"
//         onClick={() => handleViewChange("subscription")}
//         className={btnClass("subscription")}
//       >
//         <CreditCard size={18} />
//         <span>Subscription</span>
//       </button>
//     </>
//   );

//   // =====================================================
//   // CHEF MENU
//   // =====================================================

//   const ChefMenu = () => (
//     <>
//       <KitchenOrdersButton />
//     </>
//   );

//   // =====================================================
//   // MENU BASED ON ROLE
//   // =====================================================

//   const MenuButtons = () => {
//     const normalizedRole = String(role || "")
//       .trim()
//       .toUpperCase();

//     if (normalizedRole === "CHEF") {
//       return <ChefMenu />;
//     }

//     if (normalizedRole === "USER") {
//       return <PosMenu />;
//     }

//     // ADMIN: POS menu + administration menu
//     return (
//       <>
//         <PosMenu />
//         <div className="my-4 border-t border-slate-700" />
//         <AdminMenu />
//       </>
//     );
//   };

//   // =====================================================
//   // SIDEBAR HEADER
//   // =====================================================

//   const SidebarHeader = ({ mobile = false }) => (
//     <div
//       className={`flex items-center justify-between ${
//         mobile
//           ? "mb-4"
//           : "p-6 border-b border-slate-700"
//       }`}
//     >
//       <div>
//         <h1
//           className={`font-bold text-white ${
//             mobile ? "text-xl" : "text-2xl"
//           }`}
//         >
//           SwiftOrder POS
//         </h1>

//         <p className="text-xs text-slate-400 mt-1">
//           {role || "USER"}
//         </p>
//       </div>

//       {mobile && (
//         <button
//           type="button"
//           aria-label="Close navigation menu"
//           className="text-slate-300 p-2 rounded-lg hover:bg-slate-800"
//           onClick={() => setMobileOpen(false)}
//         >
//           <CloseIcon size={22} />
//         </button>
//       )}
//     </div>
//   );

//   // =====================================================
//   // MOBILE SIDEBAR
//   // =====================================================

//   const MobileSidebar = () => (
//     <div
//       className={`fixed inset-0 z-40 md:hidden ${
//         mobileOpen
//           ? "visible"
//           : "invisible pointer-events-none"
//       }`}
//       aria-hidden={!mobileOpen}
//     >
//       {/* Overlay */}
//       <button
//         type="button"
//         aria-label="Close navigation menu"
//         tabIndex={mobileOpen ? 0 : -1}
//         className={`absolute inset-0 bg-black/50 transition-opacity ${
//           mobileOpen ? "opacity-100" : "opacity-0"
//         }`}
//         onClick={() => setMobileOpen(false)}
//       />

//       {/* Drawer */}
//       <aside
//         className={`absolute left-0 top-0 bottom-0
//           w-64 max-w-[85vw] bg-slate-900 p-4
//           flex flex-col shadow-xl
//           transform transition-transform duration-200
//           ${
//             mobileOpen
//               ? "translate-x-0"
//               : "-translate-x-full"
//           }`}
//       >
//         <SidebarHeader mobile />

//         <nav className="flex-1 overflow-y-auto">
//           <MenuButtons />
//         </nav>
//       </aside>
//     </div>
//   );

//   // =====================================================
//   // DESKTOP SIDEBAR
//   // =====================================================

//   return (
//     <>
//       <MobileSidebar />

//       <aside className="
//         hidden md:flex md:flex-col md:w-64 md:shrink-0
//         bg-slate-900 text-white h-screen
//       ">
//         <SidebarHeader />

//         <nav className="p-4 flex-1 overflow-y-auto">
//           <MenuButtons />
//         </nav>
//       </aside>
//     </>
//   );
// }




















// // src/components/Sidebar.js
// import React, { useEffect, useState } from "react";

// import {
//   LayoutGrid,
//   Package,
//   Receipt,
//   Tag,
//   Users,
//   BarChart3,
//   Building2,
//   PieChart,
//   Wallet,
//   Table2,
//   CreditCard,
//   History,
//   X as CloseIcon,
// } from "lucide-react";

// import {
//   getKitchenCount,
// } from "../utils/kitchenBadge";


// export default function Sidebar({
//   currentView,
//   onViewChange,
//   role,
//   mobileOpen,
//   setMobileOpen,
// }) {

//   const [kitchenCount, setKitchenCount] =
//     useState(getKitchenCount());


//   // =====================================================
//   // KITCHEN BADGE
//   // =====================================================

//   useEffect(() => {

//     const interval =
//       setInterval(() => {

//         setKitchenCount(
//           getKitchenCount()
//         );

//       }, 1000);

//     return () =>
//       clearInterval(interval);

//   }, []);


//   // =====================================================
//   // BUTTON CLASS
//   // =====================================================

//   const btnClass = (view) =>
//     `w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 text-left transition-colors ${
//       currentView === view
//         ? "bg-blue-600 text-white"
//         : "text-slate-300 hover:bg-slate-800"
//     }`;


//   // =====================================================
//   // ADMIN MENU
//   // =====================================================

//   const AdminMenu = () => (

//     <>

//       <button
//         onClick={() =>
//           onViewChange("analytics")
//         }
//         className={btnClass("analytics")}
//       >
//         <BarChart3 size={18} />
//         <span>Dashboard</span>
//       </button>


//       <button
//         onClick={() =>
//           onViewChange("menu")
//         }
//         className={btnClass("menu")}
//       >
//         <Package size={18} />
//         <span>Menu Items</span>
//       </button>


//       <button
//         onClick={() =>
//           onViewChange("categories")
//         }
//         className={btnClass("categories")}
//       >
//         <Tag size={18} />
//         <span>Categories</span>
//       </button>


//       <button
//         onClick={() =>
//           onViewChange("users")
//         }
//         className={btnClass("users")}
//       >
//         <Users size={18} />
//         <span>Users</span>
//       </button>


//       <button
//         onClick={() =>
//           onViewChange("chefOrders")
//         }
//         className={`${btnClass("chefOrders")} relative`}
//       >

//         <Receipt size={18} />

//         <span>
//           Kitchen Orders
//         </span>

//         {kitchenCount > 0 && (

//           <span
//             className="
//               absolute
//               right-3
//               top-2
//               min-w-[18px]
//               h-[18px]
//               bg-red-600
//               text-white
//               text-xs
//               rounded-full
//               flex
//               items-center
//               justify-center
//               px-1
//             "
//           >
//             {kitchenCount}
//           </span>

//         )}

//       </button>


//       <button
//         onClick={() =>
//           onViewChange("parties")
//         }
//         className={btnClass("parties")}
//       >
//         <Users size={18} />
//         <span>Parties</span>
//       </button>


//       <button
//         onClick={() =>
//           onViewChange("expenses")
//         }
//         className={btnClass("expenses")}
//       >
//         <Wallet size={18} />
//         <span>Expenses</span>
//       </button>


//       <button
//         onClick={() =>
//           onViewChange("reports")
//         }
//         className={btnClass("reports")}
//       >
//         <PieChart size={18} />
//         <span>Reports</span>
//       </button>

// <button
//   onClick={() => onViewChange("orderHistory")}
//   className={btnClass("orderHistory")}
// >
//   <History size={18} />
//   <span>Order History</span>
// </button>

//       <button
//         onClick={() =>
//           onViewChange("profile")
//         }
//         className={btnClass("profile")}
//       >
//         <Building2 size={18} />
//         <span>Business Profile</span>
//       </button>


//       <button
//         onClick={() =>
//           onViewChange("subscription")
//         }
//         className={btnClass("subscription")}
//       >
//         <CreditCard size={18} />
//         <span>Subscription</span>
//       </button>

//     </>

//   );


//   // =====================================================
//   // POS MENU
//   // USER + ADMIN
//   // =====================================================

//   const PosMenu = () => (

//     <>

//       <button
//         onClick={() =>
//           onViewChange("pos")
//         }
//         className={btnClass("pos")}
//       >
//         <LayoutGrid size={18} />
//         <span>POS</span>
//       </button>


//       <button
//         onClick={() =>
//           onViewChange("directPos")
//         }
//         className={btnClass("directPos")}
//       >
//         <LayoutGrid size={18} />
//         <span>Direct POS</span>
//       </button>


//       <button
//         onClick={() =>
//           onViewChange("tables")
//         }
//         className={btnClass("tables")}
//       >
//         <Table2 size={18} />
//         <span>Restaurant Tables</span>
//       </button>


//       <button
//         onClick={() =>
//           onViewChange("orders")
//         }
//         className={btnClass("orders")}
//       >
//         <Receipt size={18} />
//         <span>Orders</span>
//       </button>

//     </>

//   );


//   // =====================================================
//   // CHEF MENU
//   // =====================================================

//   const ChefMenu = () => (

//     <>

//       <button
//         onClick={() =>
//           onViewChange("chefOrders")
//         }
//         className={`${btnClass("chefOrders")} relative`}
//       >

//         <Receipt size={18} />

//         <span>
//           Kitchen Orders
//         </span>

//         {kitchenCount > 0 && (

//           <span
//             className="
//               absolute
//               right-3
//               top-2
//               min-w-[18px]
//               h-[18px]
//               bg-red-600
//               text-white
//               text-xs
//               rounded-full
//               flex
//               items-center
//               justify-center
//               px-1
//             "
//           >
//             {kitchenCount}
//           </span>

//         )}

//       </button>

//     </>

//   );


//   // =====================================================
//   // BUILD MENU BASED ON ROLE
//   // =====================================================

//   const MenuButtons = () => {

//     if (role === "CHEF") {
//       return <ChefMenu />;
//     }


//     if (role === "USER") {
//       return <PosMenu />;
//     }


//     // ADMIN

//     return (
//       <>
//         <PosMenu />
//         <AdminMenu />
//       </>
//     );

//   };


//   // =====================================================
//   // MOBILE DRAWER
//   // =====================================================

//   const MobileSidebar = () => (

//     <div
//       className={`fixed inset-0 z-40 md:hidden transition-opacity ${
//         mobileOpen
//           ? "opacity-100 pointer-events-auto"
//           : "opacity-0 pointer-events-none"
//       }`}
//     >

//       <div
//         className="absolute inset-0 bg-black/40"
//         onClick={() =>
//           setMobileOpen(false)
//         }
//       />


//       <aside
//         className={`absolute left-0 top-0 bottom-0 w-64 bg-slate-900 p-4 transform transition-transform ${
//           mobileOpen
//             ? "translate-x-0"
//             : "-translate-x-full"
//         }`}
//       >

//         <div className="flex items-center justify-between mb-4">

//           <div>

//             <h1 className="text-xl font-bold text-white">
//               Restaurant POS
//             </h1>

//             <p className="text-xs text-slate-400">
//               {role}
//             </p>

//           </div>


//           <button
//             className="text-slate-300 p-2 rounded hover:bg-slate-800"
//             onClick={() =>
//               setMobileOpen(false)
//             }
//           >
//             <CloseIcon />
//           </button>

//         </div>


//         <nav>
//           <MenuButtons />
//         </nav>

//       </aside>

//     </div>
//   );


//   // =====================================================
//   // DESKTOP SIDEBAR
//   // =====================================================

//   return (

//     <>

//       <MobileSidebar />


//       <div className="
//         hidden
//         md:flex
//         md:flex-col
//         md:w-64
//         bg-slate-900
//         text-white
//         h-screen
//       ">

//         <div className="p-6 border-b border-slate-700">

//           <h1 className="text-2xl font-bold">
//             Restaurant POS
//           </h1>

//           <p className="text-xs text-slate-400 mt-1">
//             {role}
//           </p>

//         </div>


//         <nav className="p-4 flex-1 overflow-auto">

//           <MenuButtons />

//         </nav>

//       </div>

//     </>

//   );
// }
